/**
 * Free-play onboarding links — the single place that knows how a visitor with no Suikamon
 * gets into the game.
 *
 * Every "no Suikamon yet" surface (the sticky bottom banner, the inline CTA cards on News /
 * species pages, the welcome popup) routes here, so the wording ("Play", never "Hatch") and
 * the destination stay identical app-wide. Species landing pages (`/bireon`) pass their
 * species so the starter picker opens with that Suikamon already chosen — one tap from a
 * species page to naming a pet.
 */
import { getSpeciesByName } from '@shared/domain/petHelper';
import { isFreeStarterSpecies } from '@shared/domain/freeTier';
import type { SuikamonSpecies } from '@shared/domain/petModels';
import { getPetStageImageUrlForUI } from './petHelpers';

/** The free starter flow (guest play, no sign-up). */
export const PLAY_FREE_PATH = '/play-free';

/** Query parameter used to pre-select a starter species on {@link PLAY_FREE_PATH}. */
export const PLAY_FREE_SPECIES_PARAM = 'species';

/**
 * Build the free-play link, optionally pre-selecting a species.
 * An unknown/absent species id just opens the picker.
 */
export function getPlayFreePath(speciesId?: number): string {
  if (speciesId === undefined) return PLAY_FREE_PATH;
  return `${PLAY_FREE_PATH}?${PLAY_FREE_SPECIES_PARAM}=${speciesId}`;
}

/**
 * Whether a species can be picked in the free starter flow — it must be a free-tier
 * (Common) species AND released. Anything else keeps the paid adoption path.
 */
export function isPlayableForFree(species: SuikamonSpecies): boolean {
  return species.available && isFreeStarterSpecies(species);
}

/**
 * Every free-play surface previews the second evolution stage — the starter picker, the
 * onboarding banner, the welcome popup and the species page all show the same artwork.
 */
export const FREE_PREVIEW_STAGE = 2;

/** The shared free-play preview image for a species (stage clamped to the species maximum). */
export function getFreePreviewImage(species: SuikamonSpecies): string {
  return getPetStageImageUrlForUI(
    species.id,
    Math.min(FREE_PREVIEW_STAGE, species.behavior.maxStage),
  );
}

/**
 * Resolve the species a route path points at (`/bireon` → Bireon), or null when the path
 * is not a species landing page. Used by the global onboarding banner so it can speak about
 * the exact Suikamon the visitor is looking at.
 */
export function getSpeciesFromPath(pathname: string): SuikamonSpecies | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length !== 1) return null;
  return getSpeciesByName(decodeURIComponent(segments[0])) ?? null;
}
